import { IStatementsHook } from '@models';
import { StatementService } from '@services';
import { useStore } from '@stores';
import { getDateRangeFromPreset } from '@utils';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const useStatements = (): IStatementsHook => {
  const { statementStore } = useStore();
  const {
    loading,
    statements,
    totalCount,
    skipCount,
    maxResultCount,
    currentPage,
    setSkipCount,
    setCurrentPage,
  } = statementStore;

  const [number, setNumber] = useState<string>();
  const [customer, setCustomer] = useState<string>();
  const [status, setStatus] = useState<string[]>();
  const [datePreset, setDatePreset] = useState<string | undefined>();
  const [customFrom, setCustomFrom] = useState<string | undefined>();
  const [customTo, setCustomTo] = useState<string | undefined>();
  // Bumped to re-run the fetch after a mutation (log payment, edit, delete).
  const [reloadKey, setReloadKey] = useState(0);
  // ABP sorting expression from the table's date columns, e.g. 'CreationTime DESC'.
  const [sorting, setSorting] = useState<string | undefined>();

  const statusParam = useMemo(() => (status?.length ? status.join(',') : undefined), [status]);

  const { startAt, endAt } = useMemo(
    () =>
      datePreset === 'custom'
        ? { startAt: customFrom, endAt: customTo }
        : getDateRangeFromPreset(datePreset),
    [datePreset, customFrom, customTo]
  );

  const handleStatusChange = (values?: string[]) => {
    setStatus(values?.length ? values : undefined);
    setSkipCount(0);
    setCurrentPage(1);
  };

  useEffect(() => {
    return () => statementStore.resetStatements();
  }, [statementStore]);

  useEffect(() => {
    statementStore.getAll({
      customer,
      status: statusParam,
      number,
      startAt,
      endAt,
      maxResultCount,
      skipCount,
      sorting,
    });
  }, [statementStore, customer, statusParam, number, startAt, endAt, maxResultCount, skipCount, sorting, reloadKey]);

  const handlePageChange = (pagination: any) => {
    statementStore.setMaxResultCount(pagination.pageSize);
    statementStore.setSkipCount((pagination.current - 1) * pagination.pageSize);
    statementStore.setCurrentPage(pagination.current);
  };

  const handleSortChange = (value?: string) => {
    setSorting(value);
    setSkipCount(0);
    setCurrentPage(1);
  };

  // Memoized so the debounce instance is stable across re-renders (the container is an observer and
  // re-renders on every store change); otherwise rapid keystrokes land on different instances and
  // stop coalescing.
  const handleNumberSearch = useMemo(
    () =>
      debounce((text: string) => {
        setNumber(text.trim() || undefined);
        setSkipCount(0);
        setCurrentPage(1);
      }, 500),
    [setSkipCount, setCurrentPage]
  );

  const handleCustomerSearch = useMemo(
    () =>
      debounce((text: string) => {
        setCustomer(text.trim() || undefined);
        setSkipCount(0);
        setCurrentPage(1);
      }, 500),
    [setSkipCount, setCurrentPage]
  );

  const handleDatePresetChange = (preset: string) => {
    setDatePreset(preset || undefined);
    if (preset !== 'custom') {
      setCustomFrom(undefined);
      setCustomTo(undefined);
    }
    setSkipCount(0);
    setCurrentPage(1);
  };

  const handleCustomDateRange = (from?: string, to?: string) => {
    setCustomFrom(from);
    setCustomTo(to);
    setSkipCount(0);
    setCurrentPage(1);
  };

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  const fetchAllForExport = useCallback(async () => {
    const result = await StatementService.getAll({
      customer,
      status: statusParam,
      number,
      startAt,
      endAt,
      maxResultCount: totalCount || 10000,
      skipCount: 0,
      sorting,
    });
    return result.items;
  }, [customer, statusParam, number, startAt, endAt, totalCount, sorting]);

  return {
    loading,
    dataSource: Array.from(statements.values()),
    totalCount,
    maxResultCount,
    currentPage,
    handleNumberSearch,
    handleCustomerSearch,
    setStatus: handleStatusChange,
    datePreset,
    customFrom,
    customTo,
    handleDatePresetChange,
    handleCustomDateRange,
    handlePageChange,
    handleSortChange,
    sorting,
    refresh,
    fetchAllForExport,
  };
};
